<template>
  <v-form
    v-model="validForm"
    :disabled="Object.values(loading).includes(true)"
    class="ResetPasswordDrawerForm"
    @submit.prevent="onResetFormSubmit"
  >
    <v-card-text>
      <v-text-field
        v-model="formData.profileId"
        :disabled="isUpdatePasswordStep"
        :rules="rules.profileId"
        label="Email or profile id"
        outlined
        hide-details
      />
    </v-card-text>
    <v-card-actions>
      <v-btn
        v-if="isUpdatePasswordStep"
        outlined
        width="130"
        class="mr-2"
        @click="clearResetForm"
      >
        <v-icon left>restart_alt</v-icon>
        Cancel
      </v-btn>
      <v-btn
        :disabled="(!!sendCodeUnavailableUntil && !isResetCodeSent) || !formData.profileId"
        :loading="loading.sendResetCode"
        :color="isResetCodeSent ? 'green' : 'blue'"
        outlined
        class="flex-grow-1 ml-2"
        @click="onSendCodeClick"
      >
        <v-icon left>{{ isResetCodeSent ? 'mark_email_read' : 'outgoing_mail' }}</v-icon>
        <span style="min-width: 30px">{{ sendResetCodeText }}</span>
      </v-btn>
    </v-card-actions>

    <v-divider class="my-2" />

    <template v-if="isUpdatePasswordStep">
      <v-card-text class="pb-0">
        <p>
          Put your secret code received on your account email address and set new password
          to your profile
        </p>

        <v-text-field
          v-model="formData.resetCode"
          :disabled="!isUpdatePasswordStep"
          :rules="rules.resetCode"
          label="Reset code"
          outlined
        />
        <v-text-field
          v-model="formData.newPassword"
          :disabled="!isUpdatePasswordStep"
          :rules="rules.password"
          label="New password"
          outlined
        />
      </v-card-text>
      <v-card-actions>
        <v-btn
          :disabled="!isUpdatePasswordStep"
          type="submit"
          block
          color="blue"
          class="white--text"
        >
          Update password
        </v-btn>
      </v-card-actions>
    </template>
  </v-form>
</template>

<script>
import { cloneDeep } from 'lodash';
import { mapActions } from 'vuex';
import { useIntervalTimer } from '@/helpers/timers';
import { passwordRegex } from '@/helpers/regex';
import { handleReqErrorWithToast } from '@/helpers/error-handlers';

function useResetFormData() {
  return {
    profileId: '',
    resetCode: '',
    newPassword: '',
  };
}

export default {
  name: 'ResetPasswordDrawerForm',
  data() {
    return {
      validForm: false,
      formData: useResetFormData(),
      rules: {
        profileId: [
          v => !!v || 'Field is required',
        ],
        resetCode: [
          v => !!v || 'Field is required',
        ],
        password: [
          v => !!v || 'Field is required',
          v => v.length >= 8 || 'Min length: 8',
          v => passwordRegex.test(v)
            || 'Password must be at least 8 characters long and contain at least one lowercase letter, uppercase letter, number and symbol',
        ],
      },
      isUpdatePasswordStep: false,
      isResetCodeSent: false,
      sendCodeUnavailableTime: null,
      sendCodeUnavailableUntil: null,
      loading: {
        sendResetCode: false,
        updatePassword: false,
      },
    };
  },
  computed: {
    sendResetCodeText() {
      if (this.isResetCodeSent) {
        return 'Code sent';
      }
      if (this.sendCodeUnavailableUntil) {
        return this.sendCodeUnavailableTime;
      }
      return this.isUpdatePasswordStep ? 'Resend code' : 'Send code';
    },
  },
  methods: {
    ...mapActions('children', [
      'sendResetPasswordCode',
      'updateChildPassword',
    ]),
    onResetFormSubmit() {
      if (this.isUpdatePasswordStep) {
        this.updateAccountPassword();
      } else {
        this.sendResetCode();
      }
    },
    updateAccountPassword() {
      if (!this.validForm || this.loading.updatePassword) return;

      this.loading.updatePassword = true;

      const payload = cloneDeep(this.formData);

      this.updateChildPassword(payload)
        .then(() => {
          this.clearResetForm();
          this.$toast.success('Password successfully updated');
        })
        .catch(err => handleReqErrorWithToast(err, 'Failed to update password'))
        .finally(() => (this.loading.updatePassword = false));
    },
    onSendCodeClick() {
      if (this.isResetCodeSent) {
        this.isResetCodeSent = false;
        return;
      }

      this.sendResetCode();
    },
    sendResetCode() {
      if (!this.validForm || this.loading.sendResetCode) {
        return;
      }

      this.loading.sendResetCode = true;

      this.sendResetPasswordCode({ profileId: this.formData.profileId })
        .then(() => {
          this.isUpdatePasswordStep = true;
          this.isResetCodeSent = true;
          this.setSendCodeTimer();
          this.$toast.success('Reset code sent to your account email');
        })
        .catch(err => handleReqErrorWithToast(err, 'Failed to send reset code'))
        .finally(() => (this.loading.sendResetCode = false));
    },
    setSendCodeTimer() {
      useIntervalTimer('1m', (secondsLeft, timerUntil) => {
        this.sendCodeUnavailableTime = secondsLeft;
        this.sendCodeUnavailableUntil = timerUntil;
        if (!timerUntil) {
          this.isResetCodeSent = false;
        }
      });
    },
    clearResetForm() {
      Object.assign(this.formData, useResetFormData());
      this.isUpdatePasswordStep = false;
      this.isResetCodeSent = false;
    },
  },
};
</script>
